/**
 * Environment variable validation
 * Ensures all required configuration is present at startup
 */

import { z } from 'zod';
import { logger } from '@/lib/logger';

const envSchema = z.object({
  // Required Firebase configuration (canonical naming: VITE_FIREBASE_*)
  // These are validated by requireEnv.ts - this schema is for additional validation
  VITE_FIREBASE_API_KEY: z.string().min(1, 'Firebase API key is required'),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1, 'Firebase auth domain is required'),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1, 'Firebase project ID is required'),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().min(1, 'Firebase storage bucket is required'),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1, 'Firebase messaging sender ID is required'),
  VITE_FIREBASE_APP_ID: z.string().min(1, 'Firebase app ID is required'),

  // Optional but recommended
  VITE_RDW_LOOKUP_URL: z.string().url().optional(),
  VITE_GOOGLE_MAPS_API_KEY: z.string().optional(),
  VITE_POSTHOG_KEY: z.string().optional(),
  VITE_POSTHOG_HOST: z.string().url().optional(),
  VITE_SENTRY_DSN: z.string().url().optional(),

  // App URL (used for link generation — falls back to window.location.origin at runtime)
  VITE_APP_URL: z.string().url().optional(),

  // Development flags
  VITE_USE_EMULATORS: z.enum(['true', 'false']).optional(),
  VITE_USE_MOCK: z.enum(['true', 'false']).optional(),

  // Deprecated (kept for backwards compatibility)
  VITE_TEAM_ID: z.string().optional(),
});

export type Env = z.infer<typeof envSchema>;

/**
 * Validate environment variables at startup
 * Throws error if required variables are missing
 */
export function validateEnv(): Env {
  try {
    const parsed = envSchema.parse(import.meta.env);
    logger.info('✅ Environment configuration validated successfully');
    return parsed;
  } catch (error) {
    if (error instanceof z.ZodError) {
      logger.error('❌ Invalid environment configuration:');
      error.errors.forEach((err) => {
        logger.error(`  - ${err.path.join('.')}: ${err.message}`);
      });

      // Show user-friendly error
      const missingVars = error.errors.map((err) => err.path.join('.')).join(', ');

      throw new Error(
        `Missing required environment variables: ${missingVars}\n\n` +
          'Please check your .env file and ensure all required variables are set.\n' +
          'See .env.example for a template.'
      );
    }
    throw error;
  }
}

/**
 * Get a typed environment variable
 * Safe to use after validateEnv() has been called
 */
export function getEnv<K extends keyof Env>(key: K): Env[K] {
  return import.meta.env[key] as Env[K];
}

/**
 * Check if running in development mode
 */
export function isDev(): boolean {
  return import.meta.env.DEV;
}

/**
 * Check if running in production mode
 */
export function isProd(): boolean {
  return import.meta.env.PROD;
}

/**
 * Check if emulators are enabled
 */
export function useEmulators(): boolean {
  return import.meta.env.VITE_USE_EMULATORS === 'true';
}

/**
 * Check if mock data is enabled.
 * Safety: In production builds, mock mode is forcibly disabled even if the env var leaks through.
 */
export function useMock(): boolean {
  const requested = import.meta.env.VITE_USE_MOCK === 'true';
  if (requested && import.meta.env.PROD) {
    logger.error(
      '[env] VITE_USE_MOCK=true in a production build — forcibly disabled. Fix your build config.'
    );
    return false;
  }
  return requested;
}

/**
 * Get the app base URL for link generation (invites, payment redirects, shared links).
 *
 * Priority:
 * 1. VITE_APP_URL env var (set in production builds)
 * 2. window.location.origin (runtime fallback — works for any domain)
 *
 * Always returns a URL without trailing slash.
 */
export function getAppBaseUrl(): string {
  const envUrl = import.meta.env.VITE_APP_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.startsWith('http')) {
    return envUrl.replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined') {
    if (import.meta.env.PROD) {
      logger.warn(
        '[getAppBaseUrl] VITE_APP_URL not set — falling back to window.location.origin. ' +
          'Set VITE_APP_URL in your production build to ensure correct link generation.'
      );
    }
    return window.location.origin;
  }
  return 'https://app.modcar.io';
}

/**
 * Build a full app URL for a given path.
 *
 * Usage:
 *   buildAppUrl('/invite/abc123?team=xyz')  → "https://app.modcar.io/invite/abc123?team=xyz"
 *   buildAppUrl('/vehicles/123')            → "https://app.modcar.io/vehicles/123"
 *
 * Always use this instead of `window.location.origin + path` for shared/external links.
 */
export function buildAppUrl(path: string): string {
  const base = getAppBaseUrl();
  // Ensure exactly one slash between base and path
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}
